import _ from "underscore";
import iconListData from "../../json/icon_list.json";
import SkyObjectTypeMap from "./SkyObjectTypeMap";

("use strict");

const skyObjectIcon = {
  iconSubDirIndex: [
    { directory: "Stars", types: _.range(1, 10) },
    { directory: "DeepSky", types: _.range(10, 30) },
    { directory: "SolarSystem", types: _.range(30, 36) },
    { directory: "Constellations", types: [40] },
    { directory: "Asterisms", types: [41] },
    { directory: "Meteors", types: [42] },
    { directory: "Satellites", types: [35] },
  ],

  genericFileNames: [
    { fileName: "generic_star.png", types: [1, 10] },
    { fileName: "generic_doublestar.png", types: [2, 3] },
    { fileName: "generic_doublevariablestar.png", types: [4, 5] },
    { fileName: "generic_variablestar.png", types: [6] },
    { fileName: "generic_opencluster.png", types: [11] },
    { fileName: "generic_globularcluster.png", types: [12] },
    { fileName: "generic_galacticnebula.png", types: [13] },
    { fileName: "generic_darknebula.png", types: [14] },
    { fileName: "generic_planetarynebula.png", types: [15] },
    { fileName: "generic_galaxy.png", types: [16, 17, 18, 19] },
    { fileName: "generic_quasar.png", types: [20, 21, 22, 23] },
    { fileName: "generic_planet.png", types: [30] },
    { fileName: "generic_moon.png", types: [31] },
    { fileName: "generic_satellite.png", types: [32, 35] },
    { fileName: "generic_minorplanet.png", types: [33] },
    { fileName: "generic_comet.png", types: [34] },
    { fileName: "generic_constellation.png", types: [40] },
    { fileName: "generic_asterism.png", types: [41] },
    { fileName: "generic_meteorshower.png", types: [42] },
    { fileName: "generic_feature.png", types: [39] },
    { fileName: "generic_supernovaremnant.png", types: [7] },
  ],

  iconBaseUrl: "https://ssdata.starrynight.com/skysafari-data/prod",

  iconListUrl:
    "https://ssdata.starrynight.com/skysafari-data/prod/Icons/icon_list.json",

  genericIconPrefix: "/Icons/Generic/",

  genericDefaultFileName: "generic_feature.png",

  iconBaseDir: "/Icons/",

  iconFileExtension: ".jpg",

  getPath: function (skyObject) {
    const {
      type: skyObjectTypeAbbrev = "",
      name: skyObjectName = "",
      catalogNumbers: skyObjectCatalogNumbers = [],
    } = skyObject;

    const {
      type: skyObjectType = "TYPE_UNKNOWN",
      value: skyObjectTypeValue = 0,
    } = SkyObjectTypeMap.getTypeByAbbrev(skyObjectTypeAbbrev);

    if (skyObjectType === "TYPE_UNKNOWN") return this.getGenericIconPath();

    const category = this.getSubDir(skyObjectTypeValue);
    if (!category) return this.getGenericIconPath(skyObjectTypeValue);

    const { hasIcon = false, iconName = "" } = this.queryForIcon(
      category,
      skyObjectType,
      skyObjectName,
      skyObjectCatalogNumbers,
    );

    return hasIcon
      ? encodeURI(
          this.iconBaseUrl +
            this.iconBaseDir +
            category +
            "/" +
            iconName +
            this.iconFileExtension,
        )
      : this.getGenericIconPath(skyObjectTypeValue);
  },

  hydrateIconCollection: function () {
    const iconList = iconListData.icons;
    if (iconList && iconList.length > 0) {
      this.createBaseCollection();
      iconList.forEach((path) => {
        this.addToCollection(path);
      });
    }
  },

  queryForIcon: function (
    category,
    skyObjectType,
    skyObjectName,
    skyObjectCatalogNumbers,
  ) {
    let hasIcon = false;
    let iconName = this.processIconName(skyObjectType, skyObjectName);
    const catalogNumbers =
      skyObjectCatalogNumbers.length > 0
        ? skyObjectCatalogNumbers.split("%%")
        : [];
    const namesToCheck = [iconName, ...catalogNumbers];

    for (const name of namesToCheck) {
      if (this.iconExists(category, name)) {
        iconName = name;
        hasIcon = true;
        break;
      }
    }

    return { hasIcon: hasIcon, iconName: iconName };
  },

  processIconName: (type = "", name = "") => {
    if (type === "TYPE_SATELLITE" && name.startsWith("IRIDIUM")) {
      return "IRIDIUM";
    }

    if (
      type === "TYPE_COMET" &&
      name.startsWith("73P/Schwassmann-Wachmann 3")
    ) {
      return "73PSchwassmannOWachmann 3";
    }

    if (type === "TYPE_COMET" && name.startsWith("D/1993 F2")) {
      return "Shoemaker-Levy 9";
    }

    const cleanName = name.replace("/", "");

    if (cleanName.startsWith("Messier")) {
      return cleanName.replace("Messier", "M");
    }

    if (cleanName.startsWith("Sharpless 2")) {
      return cleanName.replace("Sharpless 2", "Sh 2");
    }

    return cleanName;
  },

  iconExists: (category = "", objectName = "") =>
    !!Portal.Current.Collections?.skyObjects?.icons?.[category]?.includes(
      objectName,
    ),

  getIconCategory: (key = "") => {
    const parts = key.split("/");

    return parts.length > 1 ? parts[parts.length - 2] : null;
  },

  getIconName: (key = "") => {
    const parts = key.split("/");

    return parts.length > 1
      ? parts[parts.length - 1].replace(/\.[^/.]+$/, "")
      : null;
  },

  createBaseCollection: () => {
    Portal.Current.Collections.skyObjects = {};
    Portal.Current.Collections.skyObjects.icons = {};
  },

  addToCollection: function (iconPath) {
    // Validate input
    if (!iconPath || typeof iconPath !== "string") {
      console.warn("addToCollection: Invalid iconPath provided:", iconPath);
      return;
    }

    // Ensure collection structure exists
    if (!Portal.Current?.Collections?.skyObjects?.icons) {
      console.warn("addToCollection: Collection structure not initialized");
      return;
    }

    const category = this.getIconCategory(iconPath);
    const objectName = this.getIconName(iconPath);

    // Validate extracted values
    if (!category || !objectName) {
      console.warn(
        "addToCollection: Could not extract category or objectName from path:",
        iconPath,
      );
      return;
    }

    // Initialize category array if it doesn't exist
    if (!Portal.Current.Collections.skyObjects.icons[category]) {
      Portal.Current.Collections.skyObjects.icons[category] = [];
    }

    // Add object name if not already present (avoid duplicates)
    const categoryIcons = Portal.Current.Collections.skyObjects.icons[category];
    if (!categoryIcons.includes(objectName)) {
      categoryIcons.push(objectName);
    }
  },

  getGenericIconPath: function (skyObjectTypeValue = 0) {
    if (!skyObjectTypeValue > 0) {
      return (
        this.iconBaseUrl + this.genericIconPrefix + this.genericDefaultFileName
      );
    }

    const genericFileName = this.getGenericFileName(skyObjectTypeValue);

    return encodeURI(
      this.iconBaseUrl + this.genericIconPrefix + genericFileName,
    );
  },

  getGenericFileName: function (SkyObjectTypeValue = 0) {
    const result = this.genericFileNames.find((name) =>
      name.types.includes(SkyObjectTypeValue),
    );

    return result ? result.fileName : this.genericDefaultFileName;
  },

  getSubDir: function (SkyObjectTypeValue = 0) {
    const result = this.iconSubDirIndex.find((name) =>
      name.types.includes(SkyObjectTypeValue),
    );

    return result ? result.directory : "";
  },
};

export default skyObjectIcon;
